/** Planned units, not capacity. `pounds` is a lower bound whenever `unknownSkus` > 0. */
import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { EffectiveWeightLimit, PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramPegboardPlacement } from "@CommonTypes/merchflow/pog/pegboard";
import {
  degenerateWeightStyles,
  resolveCountedWeightPounds,
  type CountedUnitStyle,
  type PackCounts,
  type ResolveWeightOptions,
  type StoredWeights,
  type WeightProvenance,
} from "@CommonUtils/pog/productWeight";

export interface WeightTotal {
  pounds: number;
  unknownSkus: number;
  measuredSkus: number;
  derivedSkus: number;
}

const emptyTotal = (): WeightTotal => ({
  pounds: 0,
  unknownSkus: 0,
  measuredSkus: 0,
  derivedSkus: 0,
});

const mergeTotals = (into: WeightTotal, from: WeightTotal): void => {
  into.pounds += from.pounds;
  into.unknownSkus += from.unknownSkus;
  into.measuredSkus += from.measuredSkus;
  into.derivedSkus += from.derivedSkus;
};

/** Per-format stamped-column screen for the whole pog, not one row. */
const stampedStylesCache = new WeakMap<Pog, ReadonlySet<CountedUnitStyle>>();

export const stampedWeightStyles = (pog: Pog): ReadonlySet<CountedUnitStyle> => {
  const cached = stampedStylesCache.get(pog);
  if (cached !== undefined) {
    return cached;
  }

  const products = [
    ...(pog.planogram?.getAllPlanogramProducts?.() ?? []),
    ...(pog.unrangedItems ?? []),
  ];
  const stamped = degenerateWeightStyles(products);

  stampedStylesCache.set(pog, stamped);
  return stamped;
};

export const weightOptionsForPog = (pog: Pog | undefined): ResolveWeightOptions =>
  pog ? { ignoredStyles: stampedWeightStyles(pog) } : {};

const weightOptionsForShelf = (shelf: PlanogramShelf): ResolveWeightOptions =>
  weightOptionsForPog(shelf.bay?.planogram?.pog);

export const weightOptionsForHover = (
  hoveredShelfItem: PlanogramItem | null | undefined,
  hoveredPegboardPlacement: PlanogramPegboardPlacement | null | undefined,
): ResolveWeightOptions =>
  weightOptionsForPog(
    hoveredShelfItem?.shelf.bay.planogram.pog ??
      hoveredPegboardPlacement?.pegboard.bay.planogram.pog,
  );

/**
 * A shelf cap as the SERVER supplied it — no default, no fallback constant.
 * The 150 lb assumption that used to live here was the limit every overload on
 * screen was painted against, including runs the optimizer judged differently.
 */
export interface ShelfLimit {
  pounds: number;
  source: EffectiveWeightLimit["source"];
}

export type WeighableShelfItem = Pick<PlanogramItem, "name" | "size" | "getUnitsOnShelf"> &
  Partial<StoredWeights> &
  Partial<PackCounts>;

export interface ItemLoad {
  pounds: number;
  provenance: WeightProvenance;
}

/** Unit weight × planned units, or null when neither stated weight nor formula can answer. */
export const itemLoad = (
  item: WeighableShelfItem,
  options: ResolveWeightOptions = {},
): ItemLoad | null => {
  const unitWeight = resolveCountedWeightPounds(item, "UNIT", options);
  if (unitWeight === null) {
    return null;
  }

  return {
    pounds: unitWeight.pounds * item.getUnitsOnShelf(),
    provenance: unitWeight.provenance,
  };
};

export const itemLoadPounds = (
  item: WeighableShelfItem,
  options: ResolveWeightOptions = {},
): number | null => itemLoad(item, options)?.pounds ?? null;

const addItemToTotal = (
  total: WeightTotal,
  item: PlanogramItem,
  options: ResolveWeightOptions,
): void => {
  const load = itemLoad(item, options);
  if (load === null) {
    total.unknownSkus += 1;
    return;
  }

  total.pounds += load.pounds;
  if (load.provenance === "measured") {
    total.measuredSkus += 1;
  } else {
    total.derivedSkus += 1;
  }
};

export const shelfLoad = (shelf: PlanogramShelf): WeightTotal => {
  const total = emptyTotal();
  const options = weightOptionsForShelf(shelf);

  for (const item of shelf.getItemsPhysicallyOnBoard()) {
    addItemToTotal(total, item, options);
  }

  return total;
};

export const bayLoad = (bay: PlanogramBay): WeightTotal => {
  const total = emptyTotal();

  for (const shelf of bay.shelves) {
    mergeTotals(total, shelfLoad(shelf));
  }

  return total;
};

export const pogLoad = (pog: Pog): WeightTotal => {
  const total = emptyTotal();

  for (const bay of pog.planogram.bays) {
    mergeTotals(total, bayLoad(bay));
  }

  return total;
};

/**
 * Null when the run supplied no cap, which renders as no verdict.
 *
 * `weightCapacity` is deliberately not a second source: a real fixture rating
 * already arrives through this one, echoed back by pogger as `source: "shelf"`
 * and reconciled with what the run used. Two branches could only drift.
 */
export const shelfLimit = (shelf: PlanogramShelf): ShelfLimit | null => {
  const served = shelf.effectiveWeightLimit;
  return served && served.pounds > 0 ? served : null;
};

/** Null when there is no limit to divide by. Under-reports rather than invents. */
export const shelfOverloadRatio = (shelf: PlanogramShelf): number | null => {
  const limit = shelfLimit(shelf);
  if (!limit) {
    return null;
  }

  return shelfLoad(shelf).pounds / limit.pounds;
};
